import api from './api';
import { connectSSE, type SSEEventHandler } from './v2-chat-api';
import type { ChatMessage } from '@/types/chat';

export type WikiArchetype =
  | 'business_profile'
  | 'data_source_profile'
  | 'routine_profile'
  | 'user_preferences';

export interface WikiTreeProfileEntry {
  pageId: string;
  title: string;
  version: number;
  pending: boolean;
}

export interface WikiTreeSourceEntry extends WikiTreeProfileEntry {
  sourceId: string;
  sourceName: string;
}

export interface WikiTreeBusiness {
  orgId: string;
  orgName: string;
  profile: WikiTreeProfileEntry | null;
  sources: WikiTreeSourceEntry[];
}

export interface WikiTreePerson extends WikiTreeProfileEntry {
  userId: string;
  name: string;
  isOwn: boolean;
}

export interface WikiTree {
  businesses: WikiTreeBusiness[];
  people: WikiTreePerson[];
}

export interface WikiPageView {
  id: string;
  orgId: string;
  orgName: string;
  archetype: WikiArchetype;
  targetId: string | null;
  sourceName: string | null;
  slug: string;
  title: string;
  version: number;
  content: string;
  updatedAt: string;
  viewingVersion: number | null;
  lastSeenVersion: number | null;
  unseen: { count: number; summaries: string[] };
  content30dAgo: string | null;
  canEdit: boolean;
}

export interface WikiRevisionEntry {
  id: string;
  version: number;
  changeType: 'create' | 'edit' | 'restore';
  summary: string | null;
  changedByType: 'user' | 'llm' | 'system';
  changedByName: string;
  createdAt: string;
  unseen: boolean;
}

export interface WikiRevisionList {
  currentVersion: number;
  lastSeenVersion: number | null;
  revisions: WikiRevisionEntry[];
}

export interface WikiDiff {
  from: { version: number; content: string };
  to: { version: number; content: string };
}

export interface WikiDiscussState {
  threadId: string | null;
  messages: ChatMessage[];
}

export const wikiApi = {
  getTree: async (): Promise<WikiTree> => {
    const { data } = await api.get('/api/wiki/tree');
    return data.data as WikiTree;
  },

  getPage: async (pageId: string, version?: number): Promise<WikiPageView> => {
    const { data } = await api.get(`/api/wiki/pages/${pageId}`, {
      params: version !== undefined ? { version } : undefined,
    });
    return data.data as WikiPageView;
  },

  getRevisions: async (pageId: string): Promise<WikiRevisionList> => {
    const { data } = await api.get(`/api/wiki/pages/${pageId}/revisions`);
    return data.data as WikiRevisionList;
  },

  getDiff: async (pageId: string, from: number, to: number): Promise<WikiDiff> => {
    const { data } = await api.get(`/api/wiki/pages/${pageId}/diff`, { params: { from, to } });
    return data.data as WikiDiff;
  },

  updatePage: async (
    pageId: string,
    content: string,
    summary?: string,
  ): Promise<{ version: number }> => {
    const { data } = await api.put(`/api/wiki/pages/${pageId}`, { content, summary });
    return data.data as { version: number };
  },

  revert: async (pageId: string, version: number): Promise<{ version: number }> => {
    const { data } = await api.post(`/api/wiki/pages/${pageId}/revert/${version}`);
    return data.data as { version: number };
  },

  markSeen: async (pageId: string, version: number): Promise<{ lastSeenVersion: number }> => {
    const { data } = await api.post(`/api/wiki/pages/${pageId}/seen`, { version });
    return data.data as { lastSeenVersion: number };
  },

  getDiscuss: async (pageId: string): Promise<WikiDiscussState> => {
    const { data } = await api.get(`/api/wiki/pages/${pageId}/discuss`);
    return data.data as WikiDiscussState;
  },

  /** Streaming Discuss turn — SSE incl. revision_saved events (D47). */
  discuss: (
    pageId: string,
    params: { message: string; mode?: 'discuss' | 'interview' },
    onEvent: SSEEventHandler,
    signal?: AbortSignal,
  ): Promise<void> => connectSSE(`/api/wiki/pages/${pageId}/discuss`, params, onEvent, signal),
};
